import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const PER_PAGE = 5;
const toastCfg = { position: 'bottom-right', autoClose: 5000, theme: 'colored' };
const faDate = v => { try { return new Date(v).toLocaleDateString('fa-IR'); } catch { return ''; } };

const avatarBg = name => {
  const colors = ['#18181B', '#A16207', '#166534', '#1D4ED8', '#6D28D9', '#9A3412'];
  let h = 0;
  for (const ch of String(name || '?')) h = (h * 31 + ch.charCodeAt(0)) % 997;
  return colors[h % colors.length];
};

function Avatar({ name, admin }) {
  const n = String(name || 'ک').trim().charAt(0);
  return (
    <span style={{
      width: 38, height: 38, borderRadius: '50%', flexShrink: 0,
      background: admin ? '#A16207' : avatarBg(name), color: '#fff',
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      fontWeight: 800, fontSize: '0.95rem',
    }}>{n}</span>
  );
}

function ReplyForm({ onSubmit, busy, guest, name, setName, autoFocus }) {
  const [text, setText] = useState('');
  return (
    <div style={{ marginTop: 8 }}>
      {guest && (
        <input value={name} onChange={e => setName(e.target.value)} placeholder="نام شما"
          style={{ width: '100%', padding: '9px 12px', borderRadius: 10, border: '1.5px solid var(--border)', fontFamily: 'inherit', background: 'var(--card)', color: 'var(--fg)', outline: 'none', boxSizing: 'border-box', fontSize: '0.82rem', marginBottom: 8 }} />
      )}
      <textarea value={text} onChange={e => setText(e.target.value)} rows={2} placeholder="پاسخ خود را بنویسید..." autoFocus={autoFocus}
        style={{ width: '100%', padding: '9px 12px', borderRadius: 10, border: '1.5px solid var(--border)', fontFamily: 'inherit', background: 'var(--card)', color: 'var(--fg)', outline: 'none', boxSizing: 'border-box', fontSize: '0.84rem', resize: 'vertical', lineHeight: 1.8 }} />
      <button onClick={() => { if (text.trim().length >= 2) { onSubmit(text.trim()); setText(''); } }}
        disabled={busy} style={{ marginTop: 6, padding: '8px 22px', borderRadius: 999, border: 'none', background: 'var(--primary)', color: 'var(--on-primary)', fontFamily: 'inherit', fontSize: '0.8rem', fontWeight: 700, cursor: 'pointer', opacity: busy ? 0.6 : 1 }}>
        ارسال پاسخ
      </button>
    </div>
  );
}

/* Shared comments box: paginated approved tree + user replies + submit. */
export function CommentsBox({ type, id, title }) {
  const [list, setList] = useState([]);
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [body, setBody] = useState('');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  const [allowGuest, setAllowGuest] = useState(true);
  const token = typeof localStorage !== 'undefined' ? localStorage.getItem('customer_token') : '';
  const auth = () => (token ? { headers: { Authorization: `Bearer ${token}` } } : {});

  const load = async (p = 1, append = false) => {
    setLoading(true);
    const r = await axios.get(`/${type}/comments/${id}`, { params: { page: p, per_page: PER_PAGE } }).catch(() => null);
    if (r && r.data) {
      setList(prev => append ? [...prev, ...(r.data.data || [])] : (r.data.data || []));
      setPage(r.data.current_page || 1);
      setLastPage(r.data.last_page || 1);
      setTotal(r.data.total || 0);
    }
    setLoading(false);
  };
  useEffect(() => { if (id) { setList([]); setPage(1); load(1, false); } }, [type, id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    axios.get('/site-settings').then(r => {
      if (r.data?.comments && 'allow_guest' in r.data.comments) setAllowGuest(!!r.data.comments.allow_guest);
    }).catch(() => {});
  }, []);

  const who = c => c.is_admin ? 'مدیریت فروشگاه' : (c.user?.name || c.name || 'کاربر');
  const masked = c => c.is_admin ? '' : (c.phone_masked || '');

  const submit = async (text, parentId = null) => {
    if (!token && !allowGuest) { toast.error('برای ثبت دیدگاه ابتدا وارد حساب کاربری خود شوید.', toastCfg); return; }
    if (!token && !name.trim()) { toast.error('نام خود را وارد کنید.', toastCfg); return; }
    setBusy(true);
    try {
      const r = await axios.post(`/${type}/comments/${id}`,
        { body: text, name: name.trim() || undefined, parent_id: parentId || undefined }, auth());
      toast.success(r.data.message || 'ثبت شد.', toastCfg);
      if (!parentId) setBody('');
      setReplyTo(null);
    } catch (e) { toast.error(e.response?.data?.message || 'خطا در ثبت.', toastCfg); }
    setBusy(false);
  };

  return (
    <div>
      <ToastContainer position="bottom-right" autoClose={5000} rtl={true} theme="colored" />
      <h3 style={{ fontWeight: 800, fontSize: '1.05rem', margin: '0 0 4px' }}>{title || 'دیدگاه‌ها'}</h3>
      <div style={{ fontSize: '0.76rem', color: 'var(--muted-fg)', marginBottom: 14 }}>{total ? `${Number(total).toLocaleString('fa-IR')} دیدگاه` : 'هنوز دیدگاهی ثبت نشده است'}</div>

      {list.map(c => (
        <div key={c.id} style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
          <Avatar name={who(c)} admin={c.is_admin} />
          <div style={{ flex: 1, minWidth: 0, background: 'var(--card)', border: '1px solid var(--border)', borderRadius: '4px 16px 16px 16px', padding: '10px 14px', boxShadow: '0 1px 2px rgba(0,0,0,0.04)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
              <span style={{ fontWeight: 800, fontSize: '0.84rem', color: c.is_admin ? 'var(--accent)' : 'var(--card-fg, var(--fg))' }}>
                {who(c)}
                {masked(c) && <span style={{ fontWeight: 400, fontSize: '0.72rem', color: 'var(--muted-fg)', marginRight: 6, direction: 'ltr', unicodeBidi: 'embed' }}>{masked(c)}</span>}
                {c.is_admin && <span style={{ fontSize: '0.68rem', fontWeight: 700, background: 'var(--muted)', border: '1px solid var(--border)', color: 'var(--accent)', borderRadius: 999, padding: '1px 8px', marginRight: 6 }}>مدیریت</span>}
              </span>
              <span style={{ fontSize: '0.7rem', color: 'var(--muted-fg)' }}>{faDate(c.created_at)}</span>
            </div>
            <div style={{ fontSize: '0.86rem', lineHeight: 1.9, whiteSpace: 'pre-line', color: 'var(--fg)' }}>{c.body}</div>
            {(token || allowGuest) && (
            <button onClick={() => setReplyTo(replyTo === c.id ? null : c.id)}
              style={{ background: 'none', border: 'none', color: '#A16207', fontFamily: 'inherit', fontSize: '0.76rem', fontWeight: 700, cursor: 'pointer', padding: '4px 0 0' }}>
              {replyTo === c.id ? 'انصراف' : 'پاسخ'}
            </button>
            )}
            {replyTo === c.id && (
              <ReplyForm busy={busy} guest={!token} name={name} setName={setName} autoFocus
                onSubmit={t => submit(t, c.id)} />
            )}
            {(c.replies || []).map(rp => (
              <div key={rp.id} style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                <Avatar name={who(rp)} admin={rp.is_admin} />
                <div style={{ flex: 1, minWidth: 0, background: 'var(--muted)', border: '1px solid var(--border)', borderRadius: '4px 14px 14px 14px', padding: '8px 12px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: 2, flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: 800, fontSize: '0.8rem', color: rp.is_admin ? 'var(--accent)' : 'var(--card-fg, var(--fg))' }}>
                      {who(rp)}
                      {masked(rp) && <span style={{ fontWeight: 400, fontSize: '0.7rem', color: 'var(--muted-fg)', marginRight: 6, direction: 'ltr', unicodeBidi: 'embed' }}>{masked(rp)}</span>}
                      {rp.is_admin && <span style={{ fontSize: '0.66rem', fontWeight: 700, background: 'var(--muted)', border: '1px solid var(--border)', color: 'var(--accent)', borderRadius: 999, padding: '1px 8px', marginRight: 6 }}>مدیریت</span>}
                    </span>
                    <span style={{ fontSize: '0.68rem', color: 'var(--muted-fg)' }}>{faDate(rp.created_at)}</span>
                  </div>
                  <div style={{ fontSize: '0.82rem', lineHeight: 1.9, whiteSpace: 'pre-line', color: 'var(--fg)' }}>{rp.body}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      {page < lastPage && (
        <button onClick={() => load(page + 1, true)} disabled={loading}
          style={{ width: '100%', padding: 11, borderRadius: 12, border: '1.5px dashed var(--border)', background: 'var(--card)', fontFamily: 'inherit', fontSize: '0.84rem', fontWeight: 700, color: 'var(--fg)', cursor: 'pointer', marginBottom: 12 }}>
          {loading ? 'در حال بارگذاری...' : 'نمایش دیدگاه‌های بیشتر'}
        </button>
      )}

      {!token && !allowGuest ? (
        <div style={{ background: 'var(--card)', border: '1.5px dashed var(--border)', borderRadius: 16, padding: 16, textAlign: 'center', fontSize: '0.84rem', color: 'var(--fg)' }}>
          برای ثبت دیدگاه ابتدا وارد حساب کاربری خود شوید.
        </div>
      ) : (
      <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 16, padding: 14, boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
        <div style={{ fontWeight: 800, fontSize: '0.88rem', marginBottom: 8 }}>دیدگاه خود را بنویسید</div>
        {!token && allowGuest && (
          <input value={name} onChange={e => setName(e.target.value)} placeholder="نام شما"
            style={{ width: '100%', padding: '10px 14px', borderRadius: 10, border: '1.5px solid var(--border)', fontFamily: 'inherit', background: 'var(--card)', color: 'var(--fg)', outline: 'none', boxSizing: 'border-box', fontSize: '0.85rem', marginBottom: 8 }} />
        )}
        <textarea value={body} onChange={e => setBody(e.target.value)} rows={3} placeholder="نظر شما درباره این محصول / مطلب..."
          style={{ width: '100%', padding: '10px 14px', borderRadius: 10, border: '1.5px solid var(--border)', fontFamily: 'inherit', background: 'var(--card)', color: 'var(--fg)', outline: 'none', boxSizing: 'border-box', fontSize: '0.85rem', resize: 'vertical', lineHeight: 1.8 }} />
        <button onClick={() => { if (body.trim().length >= 2) { submit(body.trim()); setBody(''); } }} disabled={busy}
          style={{ marginTop: 8, padding: '10px 30px', borderRadius: 999, border: 'none', background: 'var(--primary)', color: 'var(--on-primary)', fontFamily: 'inherit', fontSize: '0.85rem', fontWeight: 700, cursor: 'pointer', boxShadow: '0 2px 6px rgba(0,0,0,0.2)', opacity: busy ? 0.6 : 1 }}>
          ثبت دیدگاه
        </button>
        <div style={{ fontSize: '0.72rem', color: 'var(--muted-fg)', marginTop: 6 }}>دیدگاه پس از تایید مدیریت نمایش داده می‌شود.</div>
      </div>
      )}
    </div>
  );
}
